import { useState } from "react";
import { useBarcodeScan } from "src/hooks/useBarcodeScan";
import useUiuxStore from "src/store/useUiuxStore";
import usePDA1050_R01Store from "./Store_PDA1050_R01";

interface PDA1050_R01FormProps {
  onChangeOrder: () => void;
  onScanBarcode: (barcode: string) => Promise<boolean>;
  onCheck: () => boolean;
  onComplete: () => void;
}

interface WarehouseSearchResult {
  warehouseCd: string;
  warehouseNm: string;
}

/** 발주 재선택 및 창고 검색 버튼에서 공통으로 사용하는 돋보기 아이콘 */
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

/**
 * 선택된 발주의 입고일자/창고를 지정하고 품목 스캔, 수량 검수 및 입고 완료를 수행하는 입력 폼이다.
 * 품명은 스캔 결과로만 표시하며 사용자가 직접 수정하지 못하도록 읽기 전용으로 제공한다.
 */
const PDA1050_R01Form = ({
  onChangeOrder,
  onScanBarcode,
  onCheck,
  onComplete,
}: PDA1050_R01FormProps) => {
  const store = usePDA1050_R01Store();
  const { setCodeSearchModalVisible, setCodeSearchModalProps } = useUiuxStore();
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  /**
   * 바코드 훅에서 확정된 스캔값을 업무 로직에 전달한다.
   * 품목 검증 성공 시 연속 작업이 가능하도록 입고량 입력칸으로 포커스를 이동한다.
   *
   * @param scannedBarcode 스캐너 또는 키보드로 확정된 바코드/품목코드
   */
  const handleBarcodeScan = async (scannedBarcode: string) => {
    const isSuccess = await onScanBarcode(scannedBarcode);
    if (isSuccess) {
      setTimeout(() => {
        document.getElementById("pda1050TransportQty")?.focus();
      }, 100);
    }
  };

  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(
    handleBarcodeScan,
    {
      clearOnScan: false,
      refocusOnScan: true,
      debounceMs: 100,
      autoFocus: false,
    }
  );

  /**
   * 스캔 입력칸을 300ms 이내 두 번 누르면 모바일 소프트 키보드 입력을 활성화한다.
   * 일반적인 단일 터치는 하드웨어 스캐너 입력 모드를 유지한다.
   */
  const handleDoubleTap = () => {
    const currentTime = Date.now();
    if (currentTime - lastTapTime < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    setLastTapTime(currentTime);
  };

  /** 키보드 입력 모드를 토글하고, 활성화할 때 스캔 입력칸으로 포커스를 이동한다. */
  const toggleKeyboard = () => {
    setKeyboardEnabled((isEnabled) => !isEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  /** 렌더링 및 모달 종료가 반영된 다음 스캔 입력칸으로 포커스를 복원한다. */
  const focusScanInput = () => {
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  /**
   * 공통 코드검색 모달을 창고코드 조건으로 열고 선택 결과를 Store에 반영한다.
   * 창고 선택 완료 후 모달을 닫고 다음 품목을 바로 스캔할 수 있도록 포커스를 복원한다.
   */
  const openWarehouseSearchModal = () => {
    if (!store.selectedPDA1050_R01) return;
    setCodeSearchModalProps({
      category: "창고코드",
      search: "",
      autoSelectSingle: true,
      autoFocus: false,
      onConfirm: (selected: WarehouseSearchResult) => {
        store.setWarehouseCd(selected.warehouseCd);
        store.setWarehouseNm(selected.warehouseNm);
        setCodeSearchModalVisible(false);
        focusScanInput();
      },
      onCloseConfirm: () => setCodeSearchModalVisible(false),
    });
    setCodeSearchModalVisible(true);
  };

  /**
   * 입고량 입력 중 Enter를 누르면 검수 처리를 실행한다.
   * 검수 성공 시 다음 품목 처리를 위해 스캔 입력칸으로 포커스를 이동한다.
   *
   * @param event 입고량 input의 키보드 이벤트
   */
  const handleQtyKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (onCheck()) {
        focusScanInput();
      }
    }
  };

  const rowClass = "flex items-center gap-1";
  const labelClass = "w-20 flex-shrink-0 text-lg font-medium text-black";
  const fieldClass = "flex-1 flex gap-1";
  const inputClass = "flex-1 max-w-[173px] min-w-0 px-2 py-0.5 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500";
  const readOnlyClass = `${inputClass} bg-gray-100 cursor-not-allowed`;
  const iconButtonClass = "w-10 flex-shrink-0 px-2 py-0.5 border border-gray-300 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center justify-center";

  return (
    <div className="w-full md:w-45 bg-white p-4 pb-1 border-r border-gray-200 overflow-y-auto">
      <div className="space-y-1">
        <div className={rowClass}>
          <label className={labelClass}>발주번호</label>
          <div className={fieldClass}>
            <input
              value={store.selectedPDA1050_R01?.purchaseOrderNo || ""}
              readOnly
              className={readOnlyClass}
            />
            <button type="button" onClick={onChangeOrder} className={iconButtonClass} title="발주 다시 선택">
              <SearchIcon />
            </button>
          </div>
        </div>

        <div className={rowClass}>
          <label className={labelClass}>상호</label>
          <div className={fieldClass}>
            <input value={store.selectedPDA1050_R01?.customNm || ""} readOnly className={readOnlyClass} />
            <div className="w-10 flex-shrink-0" />
          </div>
        </div>

        <div className={rowClass}>
          <label className={labelClass}>입고일자</label>
          <div className={fieldClass}>
            <input
              type="date"
              value={store.transportDt}
              onChange={(event) => store.setTransportDt(event.target.value)}
              className={inputClass}
            />
            <div className="w-10 flex-shrink-0" />
          </div>
        </div>

        <div className={rowClass}>
          <label className={labelClass}>창고</label>
          <div className={fieldClass}>
            <input
              value={store.warehouseNm}
              readOnly
              onClick={openWarehouseSearchModal}
              placeholder="창고 선택"
              className={`${inputClass} cursor-pointer`}
            />
            <button
              type="button"
              onClick={openWarehouseSearchModal}
              disabled={!store.selectedPDA1050_R01}
              className={`${iconButtonClass} disabled:bg-gray-300 disabled:cursor-not-allowed`}
              title="창고 검색"
            >
              <SearchIcon />
            </button>
          </div>
        </div>

        <div className={rowClass}>
          <label className={labelClass}>Scan</label>
          <div className={fieldClass}>
            <input
              ref={scanInputRef}
              value={store.barcode}
              onChange={(event) => store.setBarcode(event.target.value)}
              onInput={handleInput}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              disabled={!store.selectedPDA1050_R01}
              placeholder="바코드 스캔"
              className={`${inputClass} disabled:bg-gray-100`}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              disabled={!store.selectedPDA1050_R01}
              className={`w-10 flex-shrink-0 px-2 py-0.5 border border-gray-300 rounded transition-colors disabled:bg-gray-100 disabled:text-gray-400 ${
                keyboardEnabled
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className={rowClass}>
          <label className={labelClass}>품명</label>
          <div className={fieldClass}>
            <input
              value={store.scannedItem?.itemNm || ""}
              readOnly
              className={`${readOnlyClass} !bg-gray-100`}
            />
            <div className="w-10 flex-shrink-0" />
          </div>
        </div>

        <div className={rowClass}>
          <label className={labelClass}>입고량</label>
          <div className={fieldClass}>
            <input
              id="pda1050TransportQty"
              type="number"
              value={store.transportQty || ""}
              onChange={(event) => store.setTransportQty(Number(event.target.value))}
              onKeyDown={handleQtyKeyDown}
              disabled={!store.scannedItem}
              min="0"
              step="0.00001"
              placeholder="수량"
              className="w-20 px-2 py-0.5 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <button
              type="button"
              onClick={onCheck}
              disabled={!store.scannedItem || store.transportQty <= 0 || !store.warehouseCd}
              className="flex-1 bg-blue-500 text-white px-2 py-0.5 rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
            >
              검수 {store.checkedPDA1050_R01List.length > 0 && `(${store.checkedPDA1050_R01List.length})`}
            </button>
            <button
              type="button"
              onClick={onComplete}
              disabled={store.checkedPDA1050_R01List.length === 0 || store.isLoading}
              className="flex-1 bg-green-500 text-white px-2 py-0.5 rounded text-lg font-medium hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 whitespace-nowrap"
            >
              완료
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDA1050_R01Form;
